import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { deleteMedia, updateMedia, uploadPostImage } from "../../utils/apiClient";
import { fetchMediaBlobUrl } from "../../utils/mediaUrl";
import AuthImage from "../AuthImage";

function EditPhotoModal({
  open,
  onClose,
  mode = "add",
  postUuid,
  recipeUuid,
  photo = null,
  onSaved,
  onDeleted,
}) {
  const isEdit = mode === "edit";
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploaded, setUploaded] = useState(null);
  const [description, setDescription] = useState("");
  const [isFocus, setIsFocus] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setFile(null);
    setPreviewUrl("");
    setUploaded(null);
    setDescription("");
    setIsFocus(false);
    setBusy(false);
    setError("");
  };

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }
    if (isEdit && photo) {
      setDescription(photo.description || "");
      setIsFocus(Boolean(photo.isFocus));
      setPreviewUrl(photo.src || `/media/${photo.mediaUuid}`);
      setUploaded({
        media_uuid: photo.mediaUuid,
        url: photo.src,
        description: photo.description || "",
        is_focus: Boolean(photo.isFocus),
      });
    }
  }, [open, isEdit, photo]);

  const handleFileChange = async (event) => {
    const next = event.target.files?.[0];
    if (!next) return;
    setError("");
    setFile(next);
    setUploaded(null);
    setPreviewUrl(URL.createObjectURL(next));
    setBusy(true);
    try {
      const result = await uploadPostImage(next, {
        postUuid,
        recipeUuid,
        description,
        isFocus,
      });
      setUploaded(result);
      setIsFocus(Boolean(result.is_focus));
      try {
        const blob = await fetchMediaBlobUrl(result.url);
        setPreviewUrl(blob);
      } catch {
        /* keep object URL */
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Upload mislykkedes");
      setUploaded(null);
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!uploaded?.media_uuid) return;
    setBusy(true);
    setError("");
    try {
      const updated = await updateMedia(uploaded.media_uuid, {
        description: description.trim(),
        isFocus,
      });
      onSaved?.({
        mediaUuid: updated.media_uuid,
        src: updated.url || uploaded.url,
        description: updated.description || "",
        isFocus: Boolean(updated.is_focus),
      });
      onClose?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Gemning mislykkedes");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!uploaded?.media_uuid) return;
    if (!window.confirm("Slet dette billede? Det fjernes fra indlægget.")) return;
    setBusy(true);
    setError("");
    try {
      await deleteMedia(uploaded.media_uuid);
      onDeleted?.(uploaded.media_uuid);
      onClose?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : err.message || "Sletning mislykkedes");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEdit ? "Rediger foto" : "Tilføj foto"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {!isEdit ? (
            <Button variant="outlined" component="label" disabled={busy}>
              {file ? "Vælg en anden fil" : "Vælg fil"}
              <input
                hidden
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,.heic,.heif"
                onChange={handleFileChange}
              />
            </Button>
          ) : null}

          {busy && !isEdit && !uploaded ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          ) : null}

          {previewUrl && !(busy && !isEdit && !uploaded) ? (
            <Box
              sx={{
                maxWidth: 500,
                width: "100%",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
              }}
            >
              {isEdit || String(previewUrl).startsWith("blob:") ? (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Forhåndsvisning"
                  sx={{ display: "block", maxWidth: "100%", height: "auto" }}
                />
              ) : (
                <AuthImage
                  src={previewUrl}
                  alt="Forhåndsvisning"
                  style={{ display: "block", maxWidth: "100%", height: "auto" }}
                />
              )}
            </Box>
          ) : null}

          {uploaded ? (
            <>
              <TextField
                label="Beskrivelse (valgfri)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                disabled={busy}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isFocus}
                    onChange={(e) => setIsFocus(e.target.checked)}
                    disabled={busy}
                  />
                }
                label="Fokusbillede (ét pr. indlæg/opskrift)"
              />
            </>
          ) : null}

          {error ? (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        {isEdit ? (
          <Button color="error" onClick={handleDelete} disabled={busy}>
            Slet
          </Button>
        ) : (
          <span />
        )}
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} disabled={busy}>
            Annuller
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!uploaded || busy}>
            {busy ? "Gemmer…" : isEdit ? "Gem" : "Tilføj til indlæg"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default EditPhotoModal;
